import React, { useState, useEffect } from "react";
import type { SetViewerOption } from "../../schemas/book-schemas";
import { ViewerOptionToggles } from "./ViewerOptionToggles";
import { ContentFile } from "./ContentFile";
import { FileService } from "../../../services/fileService";

interface ContentBlockProps {
  block: {
    content_type: 'html' | 'pdf' | 'scorm' | 'html_package' | 'pptx' | 'media' | 'quiz' | 'video' | 'audio' | 'image' | 'embed' | 'text' | 'list' | 'flashcard';
    payload: any;
    appearance?: {
      background?: {
        color: string;
        image?: string | null;
        fit?: 'cover' | 'contain' | 'fill';
        opacity?: number;
      };
      padding?: {
        top?: string;
        bottom?: string;
        left?: string;
        right?: string;
      };
    };
    layout?: {
      mode: 'flow' | 'fixed' | 'grid';
    };
    timing?: {
      startMs?: number;
      endMs?: number | null;
      autoplay?: boolean;
    };
  };
  sessionId: string;
  sessionName: string;
  blockIndex?: number;
  onContentUpload?: (sessionId: string, file: File, contentType: string, fileData?: any, blockIndex?: number) => void;
  onContentDelete?: (sessionId: string, contentId: string) => void;
  onSetViewerOption?: SetViewerOption;
  size?: 'default' | 'compact';
}

export const ContentBlock: React.FC<ContentBlockProps> = ({
  block,
  sessionId,
  sessionName,
  blockIndex,
  onContentUpload,
  onContentDelete,
  onSetViewerOption,
  size = 'default',
}) => {
  const isCompact = size === 'compact';
  const padding = isCompact ? 'p-3' : 'p-4';
  const spacing = isCompact ? 'space-y-2' : 'space-y-3';

  // Secure file handling state
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewRefreshToken, setPreviewRefreshToken] = useState<number>(0);

  // Determine if file is secure
  const storedFileUrl = block.payload?.file_url || "";
  const fileIdForPreview = block.payload?.file_id || "";
  const storageType = block.payload?.storage || 
    (storedFileUrl && storedFileUrl.includes("/api/secure-file") ? "secure" : "");
  const isSecureFile = storageType === "secure";

  // Effect to fetch secure file preview URL for image, video, audio
  useEffect(() => {
    let isMounted = true;

    const previewableTypes = ['image', 'video', 'audio'];
    const isPreviewableType = previewableTypes.includes(block.content_type);

    const noFileAvailable = !fileIdForPreview && !storedFileUrl;
    if (!isPreviewableType || noFileAvailable) {
      if (isMounted) {
        setPreviewUrl(noFileAvailable ? null : storedFileUrl || null);
        setPreviewError(null);
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    if (!isSecureFile) {
      if (isMounted) {
        setPreviewUrl(storedFileUrl || null);
        setPreviewError(null);
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    if (!fileIdForPreview) {
      if (isMounted) {
        setPreviewUrl(null);
        setPreviewError("Không thể tạo bản xem trước do thiếu mã tệp.");
        setIsPreviewLoading(false);
      }
      return () => {
        isMounted = false;
      };
    }

    setIsPreviewLoading(true);
    setPreviewError(null);

    FileService.getSecureFilePreviewUrl(fileIdForPreview)
      .then((url) => {
        if (!isMounted) return;
        setPreviewUrl(url);
      })
      .catch((error) => {
        if (!isMounted) return;
        console.error("Failed to fetch secure file preview URL:", error);
        setPreviewUrl(null);
        setPreviewError("Không thể tải bản xem trước. Vui lòng thử lại.");
      })
      .finally(() => {
        if (!isMounted) return;
        setIsPreviewLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [fileIdForPreview, block.content_type, isSecureFile, storedFileUrl, previewRefreshToken]);

  // Get the appropriate URL to use for rendering
  const getMediaUrl = () => {
    if (isSecureFile) {
      return previewUrl || "";
    }
    return storedFileUrl;
  };

  /**
   * Block kinds drawn here (text, audio, embed, list, flashcard) have no file
   * header of their own, so the viewer switches are rendered above them —
   * otherwise only blocks that go through ContentFile could be hidden from
   * students, while the API accepts the flag on every content type.
   */
  const withViewerHeader = (content: React.ReactNode) => {
    if (!onSetViewerOption) {
      return <>{content}</>;
    }

    return (
      <div className="space-y-2">
        <div className="flex items-center justify-end space-x-2">
          <ViewerOptionToggles
            sessionId={sessionId}
            blockIndex={blockIndex}
            block={block}
            onSetViewerOption={onSetViewerOption}
          />
        </div>
        {content}
      </div>
    );
  };

  // Text/HTML Block
  if ((block.content_type === 'text' || block.content_type === 'html') && block.payload?.html) {
    return withViewerHeader(
      <div 
        className={`prose prose-sm dark:prose-invert max-w-none ${padding} rounded-lg border border-gray-200 dark:border-gray-600`}
        style={{
          backgroundColor: block.appearance?.background?.color || '#ffffff',
          backgroundImage: block.appearance?.background?.image ? `url(${block.appearance.background.image})` : undefined,
          backgroundSize: block.appearance?.background?.fit || 'cover',
          opacity: block.appearance?.background?.opacity ?? 1,
          paddingTop: block.appearance?.padding?.top || (isCompact ? '12px' : '16px'),
          paddingBottom: block.appearance?.padding?.bottom || (isCompact ? '12px' : '16px'),
          paddingLeft: block.appearance?.padding?.left,
          paddingRight: block.appearance?.padding?.right
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: block.payload.html }} />
      </div>
    );
  }

  // Audio Block
  if (block.content_type === 'audio' && block.payload?.file_url) {
    const mediaUrl = getMediaUrl();
    return withViewerHeader(
      <div className={`bg-white dark:bg-gray-800 ${padding} rounded-lg border border-gray-200 dark:border-gray-600`}>
        <div className={spacing}>
          {block.payload.file_name && (
            <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
              <span className="text-lg">🎵</span>
              <span className="font-medium">{block.payload.file_name}</span>
            </div>
          )}
          {isPreviewLoading && (
            <div className="flex items-center justify-center py-4 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <svg className="animate-spin h-6 w-6 mx-auto mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-xs">Đang tải bản xem trước...</p>
              </div>
            </div>
          )}
          {previewError && (
            <div className="py-2 text-center text-red-600 dark:text-red-400 text-sm">
              {previewError}
            </div>
          )}
          {mediaUrl && !previewError && !isPreviewLoading && (
            <audio
              src={mediaUrl}
              controls
              className="w-full rounded-lg"
              autoPlay={block.timing?.autoplay || false}
              onError={(e) => {
                console.error('Audio failed to load:', mediaUrl);
                setPreviewError("Không thể tải audio.");
              }}
            >
              Trình duyệt của bạn không hỗ trợ audio.
            </audio>
          )}
          {block.payload.caption && (
            <p className="text-sm text-gray-600 dark:text-gray-400 italic">
              {block.payload.caption}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Embed Block (but not SCORM - SCORM uses ContentFile component)
  if (block.content_type === 'embed') {
    return withViewerHeader(
      <div className={`bg-white dark:bg-gray-800 ${padding} rounded-lg border border-gray-200 dark:border-gray-600`}>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <span className="text-lg">📚</span>
            <span className="font-medium">Embedded Content</span>
          </div>
          {block.payload?.fileName && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {block.payload.fileName}
            </p>
          )}
          {block.payload?.url && (
            <p className="text-xs text-blue-600 dark:text-blue-400 break-all">
              {block.payload.url}
            </p>
          )}
        </div>
      </div>
    );
  }

  // List Block
  if (block.content_type === 'list' && block.payload?.list) {
    return withViewerHeader(
      <div className={`bg-white dark:bg-gray-800 ${padding} rounded-lg border border-gray-200 dark:border-gray-600`}>
        {block.payload.import_metadata?.typePreview === 'numbered_list' ? (
          <ol className="list-decimal list-inside space-y-2">
            {block.payload.list.map((item: any, index: number) => (
              <li key={item._id || index} className="text-gray-900 dark:text-gray-100">
                <span dangerouslySetInnerHTML={{ __html: item.content }} />
              </li>
            ))}
          </ol>
        ) : (
          <ul className="list-disc list-inside space-y-2">
            {block.payload.list.map((item: any, index: number) => (
              <li key={item._id || index} className="text-gray-900 dark:text-gray-100">
                <span dangerouslySetInnerHTML={{ __html: item.content }} />
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  // Flashcard Block
  if (block.content_type === 'flashcard' && block.payload?.cards) {
    const cards = block.payload.cards || [];
    const cardCount = cards.length;
    
    // Helper function to strip HTML tags
    const stripHtml = (html: string): string => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    // Helper function to truncate text
    const truncateText = (text: string, maxLength: number): string => {
      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    };

    // Show preview of first 2 cards in compact mode, 3 in default mode
    const maxPreviewCards = isCompact ? 2 : 3;
    const previewCards = cards.slice(0, maxPreviewCards);
    
    // Component to handle secure image loading for flashcard
    const FlashcardImage: React.FC<{
      side: any;
      altText: string;
    }> = ({ side, altText }) => {
      const [imageUrl, setImageUrl] = useState<string | null>(null);
      const [loading, setLoading] = useState<boolean>(true);
      const [error, setError] = useState<boolean>(false);

      useEffect(() => {
        let isMounted = true;

        const loadImage = async () => {
          if (!side?.file_url && !side?.file_id) {
            if (isMounted) {
              setLoading(false);
              setError(true);
            }
            return;
          }

          // Check if it's a secure file
          const isSecure = side.storage === 'secure' || 
            (side.file_url && side.file_url.includes('/api/secure-file'));

          if (isSecure && side.file_id) {
            // Fetch secure URL
            setLoading(true);
            try {
              const secureUrl = await FileService.getSecureFilePreviewUrl(side.file_id);
              if (isMounted) {
                setImageUrl(secureUrl);
                setLoading(false);
              }
            } catch (err) {
              console.error('Failed to fetch secure flashcard image:', err);
              if (isMounted) {
                setError(true);
                setLoading(false);
              }
            }
          } else {
            // Use direct URL for non-secure files
            if (isMounted) {
              setImageUrl(side.file_url);
              setLoading(false);
            }
          }
        };

        loadImage();

        return () => {
          isMounted = false;
        };
      }, [side?.file_id, side?.file_url, side?.storage]);

      if (loading) {
        return (
          <div className="relative w-full aspect-[4/3] bg-gray-100 dark:bg-gray-700 rounded overflow-hidden flex items-center justify-center">
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        );
      }

      if (error || !imageUrl) {
        return (
          <div className="relative w-full aspect-[4/3] bg-gray-100 dark:bg-gray-700 rounded overflow-hidden flex items-center justify-center">
            <span className="text-xs text-gray-400">Failed to load</span>
          </div>
        );
      }

      return (
        <div className="relative w-full aspect-[4/3] bg-gray-100 dark:bg-gray-700 rounded overflow-hidden">
          <img 
            src={imageUrl} 
            alt={altText} 
            className="w-full h-full object-contain"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              setError(true);
            }}
          />
        </div>
      );
    };
    
    return withViewerHeader(
      <div 
        className={`${padding} rounded-lg border border-yellow-200 dark:border-yellow-800`}
        style={{
          backgroundColor: block.appearance?.background?.color || '#fef3c7',
          paddingTop: block.appearance?.padding?.top || (isCompact ? '12px' : '16px'),
          paddingBottom: block.appearance?.padding?.bottom || (isCompact ? '12px' : '16px'),
          paddingLeft: block.appearance?.padding?.left,
          paddingRight: block.appearance?.padding?.right
        }}
      >
        <div className="space-y-3">
          {/* Header */}
          <div className="flex items-center gap-3 mb-2">
            <span className="text-2xl">🃏</span>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Flashcard Set
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {cardCount} card{cardCount !== 1 ? 's' : ''} - Interactive in reader
              </p>
            </div>
          </div>

          {/* Card Previews */}
          <div className={isCompact ? 'space-y-2' : 'space-y-3'}>
            {previewCards.map((card: any, index: number) => {
              const frontIsImage = card.front?.kind === 'image' && (card.front.file_url || card.front.file_id);
              const backIsImage = card.back?.kind === 'image' && (card.back.file_url || card.back.file_id);
              const frontIsText = card.front?.kind === 'text' && card.front.html;
              const backIsText = card.back?.kind === 'text' && card.back.html;

              return (
                <div key={card.id || index} className="bg-white dark:bg-gray-700 rounded-lg border border-yellow-300 dark:border-yellow-700 overflow-hidden">
                  <div className="px-3 py-2 bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Card {index + 1}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-2">
                    {/* Front Side */}
                    <div className="border border-gray-200 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-800">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Front</div>
                      {frontIsImage && card.front ? (
                        <FlashcardImage 
                          side={card.front} 
                          altText={card.front.file_name || `Card ${index + 1} front`}
                        />
                      ) : frontIsText && card.front?.html ? (
                        <div className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                          {truncateText(stripHtml(card.front.html), 60)}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 italic">No content</div>
                      )}
                    </div>
                    
                    {/* Back Side */}
                    <div className="border border-gray-200 dark:border-gray-600 rounded p-2 bg-gray-50 dark:bg-gray-800">
                      <div className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1">Back</div>
                      {backIsImage && card.back ? (
                        <FlashcardImage 
                          side={card.back} 
                          altText={card.back.file_name || `Card ${index + 1} back`}
                        />
                      ) : backIsText && card.back?.html ? (
                        <div className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2">
                          {truncateText(stripHtml(card.back.html), 60)}
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 italic">No content</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Show remaining cards count if there are more */}
            {cardCount > maxPreviewCards && (
              <div className="text-center py-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  +{cardCount - maxPreviewCards} more card{cardCount - maxPreviewCards !== 1 ? 's' : ''}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // For all other content types (PDF, PPTX, etc.), use ContentFile
  return (
    <div className="p-0">
      <ContentFile
        key={`${sessionId}-block-${blockIndex ?? 'single'}`}
        sessionId={sessionId}
        sessionName={sessionName}
        onContentUpload={onContentUpload}
        onContentDelete={onContentDelete}
        onSetViewerOption={onSetViewerOption}
        blockIndex={blockIndex}
        currentFile={block.payload?.file_url ? {
          file_id: block.payload.file_id || '',
          file_name: block.payload.file_name || '',
          file_type: block.content_type,
          file_size: 0,
          file_url: block.payload.file_url,
        } : undefined}
        contentBlock={{
          content_type: block.content_type as any,
          payload: block.payload
        }}
      />
    </div>
  );
};

